import moment from "moment";
import unionBy from "lodash/unionBy";

import { logger } from "@/helpers/loggerHelper";
import { ASSET_TYPE, PRINT_TYPE } from "./rrdConstants";
import { validateStoreCopyQuantity } from "./rrdValidationHelper";
import {
  calculateRRDStoreCopyCost,
  getRRDStoreCopyDefaultQuantity,
  getRRDStoreCopyFormFields,
  resolveRRDStoreCopyConfig,
} from "./rrdStoreCopyConfigHelper";
import {
  doRRDPrintTypesMatch,
  getRRDAssetCountFromSelection,
  getRRDLayoutSlots,
  normalizeRRDLocationAssets,
  normalizeRRDPrintTypeContext,
  supportsRRDDifferentExpiry,
} from "./rrdLayoutHelper";

type RRDRecord = Record<string, any>;
type RRDLocationCollection = RRDRecord[] | Record<string, RRDRecord>;

export const rrdDirectMailCampaignHelper = {
  getInitialValues,
  getExpiryDateConstraints,
  getConstrainedCouponExpiryValue,
  getCouponExpirationSetBy,
  getFixedCouponDurationDays,
  isBrandSetCouponExpiration,
  getBrandSetCouponExpiryDate,
  fetchFullyFilledLocations,
  isFullyFilledLocation,
  formatSelectedLocations,
  getLocationsTotalBudget,
  isBulkSelectionValid,
  getDisableLocationsWhileBulkSelection,
  canJoinRRDCampaign,
  getLocationAssets,
  getAllAssets,
  getLocationName,
  getFilteredAssets,
  calculateLocationCosts,
  getAssetsBelongsToLocation,
  getWeeklyAvailabilityData,
  getSingleLocationAvailabilityData,
  normalizeRRDPrintTypeContext,
  normalizeRRDLocationAssets,
};

function canJoinRRDCampaign(campaign: any) {
  return !!campaign;
}

// Calculations
function calculateDistributionCost(
  distribution = false,
  distributionCostAdmin = 0
) {
  return distribution ? distributionCostAdmin : 0;
}

function calculateStoreCopyCost(
  storeCopy = false,
  storeCopyQnt = 0,
  storeCopyConfig?: RRDRecord
) {
  return calculateRRDStoreCopyCost({
    storeCopy,
    storeCopyQnt,
    config: storeCopyConfig,
  });
}

function getAndCheckMailingDateBelongsToMailingWeeks(
  mailingDate: any,
  adminMailingWeeks: any[] = []
) {
  return mailingDate &&
    moment(mailingDate).isValid() &&
    adminMailingWeeks?.includes(mailingDate)
    ? mailingDate
    : null;
}

function getCouponExpirationDate(
  expiryDate: any,
  mailingDate: any,
  minCouponDurationAdmin: any,
  lastCouponExpiryDateAdmin: any,
  campaign?: RRDRecord
) {
  if (isBrandSetCouponExpiration(campaign)) {
    return getBrandSetCouponExpiryDate(
      mailingDate,
      getFixedCouponDurationDays(campaign)
    );
  }

  const { minCouponExpiryDate, maxCouponExpiryDate, defaultDate } =
    getExpiryDateConstraints(
      mailingDate,
      minCouponDurationAdmin,
      lastCouponExpiryDateAdmin
    );

  return (
    getConstrainedCouponExpiryValue(expiryDate, {
      minCouponExpiryDate,
      maxCouponExpiryDate,
      defaultDate,
    }) || null
  );
}

function calculateLocationTotalCost(distributionCost = 0, storeCopyCost = 0) {
  return distributionCost + storeCopyCost;
}

// End Calculations

// Initial Values
function getInitialValues(campaign: RRDRecord, draftData: RRDRecord = {}) {
  try {
    const { eligible_location } = campaign;
    const { selections } = draftData || {};
    const d = eligible_location?.reduce((t: RRDRecord, l: RRDRecord) => {
      const draftDataLocation = selections?.find((s) => s.locationId === l.id);
      const storeCopyConfig = resolveRRDStoreCopyConfig({
        location: l,
        campaign,
      });
      const storeCopyFields = getRRDStoreCopyFormFields(storeCopyConfig);
      const hasDraftStoreCopy = draftDataLocation?.storeCopy ? true : false;
      const fixedStoreCopyQnt = getRRDStoreCopyDefaultQuantity(storeCopyConfig);
      const draftStoreCopyQnt = draftDataLocation?.storeCopyQnt || "";
      const storeCopyQnt =
        hasDraftStoreCopy && fixedStoreCopyQnt
          ? fixedStoreCopyQnt
          : hasDraftStoreCopy &&
              !validateStoreCopyQuantity({
                storeCopyQnt: draftStoreCopyQnt,
                storeCopy: true,
                config: storeCopyConfig,
              })
            ? draftStoreCopyQnt
            : "";

      const distributionCost = calculateDistributionCost(
        draftDataLocation?.distribution ? true : false,
        l?.distribution?.cost
      );

      const storeCopyCost = calculateStoreCopyCost(
        hasDraftStoreCopy,
        storeCopyQnt,
        storeCopyConfig
      );

      const mailingDate = getAndCheckMailingDateBelongsToMailingWeeks(
        draftDataLocation?.mailingDate || null,
        l?.mailing_weeks
      );

      const frontCouponExpiryDate = getCouponExpirationDate(
        draftDataLocation?.frontCouponExpiryDate || null,
        mailingDate,
        campaign?.adminMinimumCouponDurationDays,
        campaign?.adminCouponExpirationDate,
        campaign
      );

      const backCouponExpiryDate = getCouponExpirationDate(
        draftDataLocation?.backCouponExpiryDate || null,
        mailingDate,
        campaign?.adminMinimumCouponDurationDays,
        campaign?.adminCouponExpirationDate,
        campaign
      );

      const locationTotalCost = calculateLocationTotalCost(
        distributionCost,
        storeCopyCost
      );

      const locationAssets = normalizeRRDLocationAssets(l);

      return {
        ...t,
        [l.id]: {
          location_id: l.id,
          location_name: l.location_name,
          system_generated_location_id: l.sys_location_id,
          hero_images: draftDataLocation?.heroImages || [],
          coupons: draftDataLocation?.coupons || [],
          distribution: draftDataLocation?.distribution ? true : false,
          estimated_distribution: l?.distribution?.quantity,
          distribution_cost: distributionCost,
          store_copy: hasDraftStoreCopy,
          store_copy_qnt: storeCopyQnt,
          store_copy_cost: storeCopyCost,
          ...storeCopyFields,
          mailing_date: mailingDate,
          couponExpirationSetBy: getCouponExpirationSetBy(campaign),
          fixedCouponDurationDays: getFixedCouponDurationDays(campaign),
          different_coupon_expiry: draftDataLocation?.differentCouponExpiry
            ? true
            : false,
          front_coupon_expiry_date: frontCouponExpiryDate,
          back_coupon_expiry_date: backCouponExpiryDate,

          // location total cost
          total_cost: locationTotalCost || 0,

          // location print type
          print_type: l.print_type,
          print_type_id: l.print_type_id || l.printTypeId || null,
          print_type_label:
            l.print_type_display ||
            l.print_type_label ||
            l.printTypeLabel ||
            null,
          layout_key: l.layout_key || null,
          fixed_images: locationAssets.fixedImages,
          orderId: draftDataLocation?.orderId || null,
        },
      };
    }, {});
    return d;
  } catch (err) {
    logger.error("Error while creating initial data for the rrd", err);
    return {};
  }
}

function getExpiryDateConstraints(
  mailingDate: any,
  minCouponDurationAdmin: any,
  lastCouponExpiryDateAdmin: any
) {
  const base = moment(mailingDate);
  if (!base.isValid()) {
    return {
      minCouponExpiryDate: null,
      defaultDate: null,
      maxCouponExpiryDate: null,
    };
  }

  const DEFAULT_DURATION = 30;
  const DEFAULT_MAX_DURATION = 90;

  // ---- Anchors ----
  const startOfNextWeek = base.clone().add(1, "week").startOf("week");
  const fridayOfMailingWeek = base.clone().day(5).startOf("day");

  // ---- MIN ----
  const adminMinDate = minCouponDurationAdmin
    ? fridayOfMailingWeek.clone().add(minCouponDurationAdmin, "days")
    : startOfNextWeek;

  let minDate = moment.max(startOfNextWeek, adminMinDate);

  // ---- MAX ----
  const defaultMaxDate = fridayOfMailingWeek
    .clone()
    .add(DEFAULT_MAX_DURATION, "days");

  const adminMaxDate =
    lastCouponExpiryDateAdmin && moment(lastCouponExpiryDateAdmin)?.isValid()
      ? moment(lastCouponExpiryDateAdmin).startOf("day")
      : null;

  // ---- MAX ---- use the admin last coupon expiration when provided,
  // otherwise fall back to the frontend 90-day limit.
  const maxDate = adminMaxDate || moment.max(defaultMaxDate, startOfNextWeek);

  if (adminMaxDate && minDate.isAfter(maxDate, "day")) {
    minDate = maxDate.clone();
  }

  // ---- DEFAULT ----
  let defaultDate = fridayOfMailingWeek.clone().add(DEFAULT_DURATION, "days");

  // ---- CLAMP DEFAULT ----
  defaultDate = moment.min(moment.max(defaultDate, minDate), maxDate);

  return {
    minCouponExpiryDate: minDate,
    defaultDate,
    maxCouponExpiryDate: maxDate,
  };
}

function getCouponExpirationSetBy(campaign?: RRDRecord | null) {
  const rawValue = campaign?.adminCouponExpirationMode;

  if (typeof rawValue !== "string") {
    return "participant";
  }

  const normalized = rawValue.trim().toLowerCase();
  return normalized === "brand" || normalized === "brand_set"
    ? "brand"
    : "participant";
}

function getFixedCouponDurationDays(campaign?: RRDRecord | null) {
  const rawValue = campaign?.adminFixedCouponDurationDays;

  const durationDays = Number(rawValue);
  return Number.isFinite(durationDays) && durationDays > 0
    ? durationDays
    : null;
}

function isBrandSetCouponExpiration(campaign?: RRDRecord | null) {
  return (
    getCouponExpirationSetBy(campaign) === "brand" &&
    !!getFixedCouponDurationDays(campaign)
  );
}

function getBrandSetCouponExpiryDate(
  mailingDate: any,
  fixedCouponDurationDays: any
) {
  const base = moment(mailingDate);
  const durationDays = Number(fixedCouponDurationDays);

  if (!base.isValid() || !Number.isFinite(durationDays) || durationDays <= 0) {
    return "";
  }

  const durationDate = base
    .clone()
    .day(5)
    .startOf("day")
    .add(durationDays, "days");
  const daysUntilSunday = (7 - durationDate.day()) % 7;

  return durationDate.add(daysUntilSunday, "days").format("YYYY-MM-DD");
}

function getConstrainedCouponExpiryValue(
  expiryDate: any,
  expiryDateConstraints?: RRDRecord
) {
  const { minCouponExpiryDate, maxCouponExpiryDate, defaultDate } =
    expiryDateConstraints || {};

  if (!minCouponExpiryDate || !maxCouponExpiryDate) {
    return expiryDate || "";
  }

  const expiryDateMoment = moment(expiryDate);
  const isValidExpiryDate =
    expiryDate &&
    expiryDateMoment.isValid() &&
    expiryDateMoment.isBetween(
      moment(minCouponExpiryDate).format("YYYY-MM-DD"),
      moment(maxCouponExpiryDate).format("YYYY-MM-DD"),
      "day",
      "[]"
    );

  return isValidExpiryDate
    ? expiryDate
    : defaultDate?.format("YYYY-MM-DD") || "";
}

function isFullyFilledLocation(location?: RRDRecord | null) {
  if (!location) return false;

  // check is quantity is multiple of 1000
  // store_copy requires distribution to be ON
  if (location?.store_copy && !location?.distribution) return false;

  const hasValidStoreCopyQuantity = !validateStoreCopyQuantity({
    storeCopyQnt: location.store_copy_qnt,
    storeCopy: location.store_copy,
    storeCopyConfig: location,
  });

  const hasBudget = location?.distribution_cost || location?.store_copy_cost;
  const hasMailingDate = location?.mailing_date;
  const { layoutKey, layoutSpec } = normalizeRRDPrintTypeContext(location);
  const hasDifferentExpiry = supportsRRDDifferentExpiry(layoutKey);
  const hasBrandSetCouponExpiry = isBrandSetCouponExpiration(location);
  const hasCouponExpiry = hasBrandSetCouponExpiry
    ? location.front_coupon_expiry_date
    : location?.different_coupon_expiry && hasDifferentExpiry
      ? location.front_coupon_expiry_date && location.back_coupon_expiry_date
      : location.front_coupon_expiry_date;

  const completionAssets = layoutSpec?.completionAssets || {
    heroImages: 0,
    coupons: 0,
  };

  const hasValidNumberOfHeroImages =
    !completionAssets.heroImages ||
    getRRDAssetCountFromSelection(location, ASSET_TYPE.HERO) ===
      completionAssets.heroImages;
  const hasValidNumberOfLargeHeroImages =
    !completionAssets.largeHeroImages ||
    getRRDAssetCountFromSelection(location, ASSET_TYPE.LARGE_HERO) ===
      completionAssets.largeHeroImages;
  const hasValidNumberOfMediumHeroImages =
    !completionAssets.mediumHeroImages ||
    getRRDAssetCountFromSelection(location, ASSET_TYPE.MEDIUM_HERO) ===
      completionAssets.mediumHeroImages;
  const hasValidNumberOfCoupons =
    !completionAssets.coupons ||
    getRRDAssetCountFromSelection(location, ASSET_TYPE.COUPON) ===
      completionAssets.coupons;

  return hasValidStoreCopyQuantity &&
    hasBudget &&
    hasMailingDate &&
    hasCouponExpiry &&
    hasValidNumberOfCoupons &&
    hasValidNumberOfHeroImages &&
    hasValidNumberOfLargeHeroImages &&
    hasValidNumberOfMediumHeroImages
    ? true
    : false;
}

function fetchFullyFilledLocations(locations: unknown) {
  const locs = Array.isArray(locations)
    ? locations
    : Object.values((locations as Record<string, RRDRecord>) || {});
  return locs.filter((l: RRDRecord) => isFullyFilledLocation(l));
}

function asArray<T = any>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

function formatPayloadAsset(asset: any) {
  if (!asset) return asset;

  const payloadAsset = { ...asset };
  delete payloadAsset.assetType;
  delete payloadAsset.couponNumber;
  delete payloadAsset.slotId;
  return payloadAsset;
}

function formatPayloadAssets(assets: unknown) {
  return asArray(assets).map(formatPayloadAsset);
}

function hasHeroSlot(layoutSlots: RRDRecord[], side: string) {
  return layoutSlots.some(
    (slot) =>
      slot.side === side &&
      [ASSET_TYPE.HERO, ASSET_TYPE.LARGE_HERO, ASSET_TYPE.MEDIUM_HERO].includes(
        slot.assetType
      )
  );
}

function getCouponSlotCount(layoutSlots: RRDRecord[], side: string) {
  return layoutSlots.filter(
    (slot) => slot.side === side && slot.assetType === ASSET_TYPE.COUPON
  ).length;
}

function getCouponsForSide(coupons: RRDRecord[], side: string, count: number) {
  if (!count) return [];

  const sideCoupons = coupons.filter((coupon) => coupon.side === side);
  return (sideCoupons.length ? sideCoupons : coupons).slice(0, count);
}

function getLayoutDrivenSelectedAssets(location: RRDRecord) {
  const { layoutKey } = normalizeRRDPrintTypeContext(location);
  const layoutSlots = getRRDLayoutSlots(layoutKey);
  const heroImages = asArray(location.hero_images);
  const coupons = asArray(location.coupons);

  if (!layoutSlots.length) {
    return {
      font: {
        hero: formatPayloadAsset(
          heroImages.find((h) => h.side === "front") || null
        ),
        coupons: formatPayloadAssets(coupons),
      },
      back: {
        hero: formatPayloadAsset(
          heroImages.find((h) => h.side === "back") || null
        ),
        coupons: formatPayloadAssets(coupons),
      },
    };
  }

  return {
    font: {
      hero: formatPayloadAsset(
        hasHeroSlot(layoutSlots, "front")
          ? heroImages.find((hero) => hero.side === "front") || null
          : null
      ),
      coupons: formatPayloadAssets(
        getCouponsForSide(
          coupons,
          "front",
          getCouponSlotCount(layoutSlots, "front")
        )
      ),
    },
    back: {
      hero: formatPayloadAsset(
        hasHeroSlot(layoutSlots, "back")
          ? heroImages.find((hero) => hero.side === "back") || null
          : null
      ),
      coupons: formatPayloadAssets(
        getCouponsForSide(
          coupons,
          "back",
          getCouponSlotCount(layoutSlots, "back")
        )
      ),
    },
  };
}

function formatSelectedLocations(
  locations: RRDLocationCollection,
  campaign: RRDRecord
) {
  const locs = Array.isArray(locations) ? locations : Object.values(locations);
  return locs.map((l: RRDRecord) => {
    const { eligible_location } = campaign;
    const location = eligible_location.find((loc) => loc.id === l.location_id);
    const layoutSelection = getLayoutDrivenSelectedAssets(l);

    const getBackCouponExpiryDate = (
      source: RRDRecord,
      differentCouponExpiry: any,
      frontCouponExpiryDate: any,
      backCouponExpiryDate: any
    ) => {
      if (isBrandSetCouponExpiration(source)) {
        return frontCouponExpiryDate;
      }

      const { layoutKey, printType } = normalizeRRDPrintTypeContext(source);
      if (printType === PRINT_TYPE.POSTCARD.value) return "";

      if (supportsRRDDifferentExpiry(layoutKey)) {
        return !differentCouponExpiry || !backCouponExpiryDate
          ? frontCouponExpiryDate
          : backCouponExpiryDate;
      }

      return frontCouponExpiryDate;
    };

    return {
      locationInfo: location,
      // total_cost: (l.distribution_cost || 0) + (l.store_copy_cost || 0),
      selectedAssets: layoutSelection,

      // key changed
      locationId: l.location_id,
      locationName: l.location_name,
      systemGeneratedLocationId: l.sys_location_id,
      sysLocationId: l.sys_location_id,
      heroImages: formatPayloadAssets(l.hero_images),
      fixedImages: formatPayloadAssets(l.fixed_images),
      coupons: formatPayloadAssets(l.coupons),
      distribution: l.distribution,
      estimatedDistribution: l?.estimated_distribution,
      distributionCost: l.distribution_cost,
      storeCopy: l.store_copy,
      storeCopyQnt: l.store_copy_qnt,
      storeCopyCost: l.store_copy_cost,
      storeCopyCpm: l.store_copy_cpm,
      mailingDate: l.mailing_date,
      couponExpirationSetBy: getCouponExpirationSetBy(l),
      fixedCouponDurationDays: getFixedCouponDurationDays(l),

      differentCouponExpiry: isBrandSetCouponExpiration(l)
        ? false
        : l.different_coupon_expiry,
      frontCouponExpiryDate: l.front_coupon_expiry_date,
      backCouponExpiryDate: getBackCouponExpiryDate(
        l,
        l.different_coupon_expiry,
        l.front_coupon_expiry_date,
        l.back_coupon_expiry_date
      ),

      // location total cost
      totalCost: l.total_cost,

      // location print type
      printType: l.print_type,
      printTypeId: l.print_type_id,
      printTypeLabel:
        normalizeRRDPrintTypeContext(l).printTypeLabel || l.print_type_label,
      layout_key: l.layout_key,
      orderId: l.orderId,
    };
  });
}

function getLocationsTotalBudget(locations: RRDLocationCollection) {
  const locs = Array.isArray(locations) ? locations : Object.values(locations);
  return locs.reduce((t: number, l: RRDRecord) => {
    const total_cost =
      l.total_cost || (l.distribution_cost || 0) + (l.store_copy_cost || 0);

    return t + total_cost;
  }, 0);
}

function getLocationAssets(location: RRDRecord) {
  return normalizeRRDLocationAssets(location);
}

function getAllAssets(
  locations: RRDLocationCollection,
  printType?: string | null,
  market?: string | null
) {
  let locs = Array.isArray(locations) ? locations : Object.values(locations);

  if (printType || market) {
    locs = locs.filter((l: RRDRecord) => {
      return (
        (!printType || doRRDPrintTypesMatch(l.print_type, printType)) &&
        (!market || l.market_name === market)
      );
    });
  }

  return locs.reduce(
    (acc: RRDRecord, l: RRDRecord) => {
      const {
        heroImages,
        largeHeroImages,
        mediumHeroImages,
        fixedImages,
        coupons,
      } = getLocationAssets(l);

      acc.heroImages = unionBy([...acc.heroImages, ...heroImages], "id");
      acc.largeHeroImages = unionBy(
        [...acc.largeHeroImages, ...largeHeroImages],
        "id"
      );
      acc.mediumHeroImages = unionBy(
        [...acc.mediumHeroImages, ...mediumHeroImages],
        "id"
      );
      acc.fixedImages = unionBy([...acc.fixedImages, ...fixedImages], "id");
      acc.coupons = unionBy([...acc.coupons, ...coupons], "id");

      return acc;
    },
    {
      heroImages: [],
      largeHeroImages: [],
      mediumHeroImages: [],
      fixedImages: [],
      coupons: [],
    }
  );
}

function getLocationName(location: RRDRecord) {
  const { printTypeLabel } = normalizeRRDPrintTypeContext(location);

  const marketName = location.market_name ? ` : ${location.market_name}` : "";
  const printTypeName = printTypeLabel ? ` : ${printTypeLabel}` : "";

  return `${location?.location_name}${marketName}${printTypeName}`;
}

function getFilteredAssets(
  assets: RRDRecord = {},
  selectedLabel: unknown[] = []
) {
  const {
    heroImages = [],
    largeHeroImages = [],
    mediumHeroImages = [],
    fixedImages = [],
    coupons = [],
  } = assets;

  if (selectedLabel.length === 0) {
    return {
      heroImages,
      largeHeroImages,
      mediumHeroImages,
      fixedImages,
      coupons,
    };
  }

  const labelSet = new Set(selectedLabel);

  const hasMatchedLabel = ({ labels }: RRDRecord) =>
    Array.isArray(labels) && labels.some((l) => labelSet.has(l));

  return {
    heroImages: heroImages.filter(hasMatchedLabel),
    largeHeroImages: largeHeroImages.filter(hasMatchedLabel),
    mediumHeroImages: mediumHeroImages.filter(hasMatchedLabel),
    fixedImages: fixedImages.filter(hasMatchedLabel),
    coupons: coupons.filter(hasMatchedLabel),
  };
}

function calculateLocationCosts(
  values: RRDRecord,
  storeCopyCpm: any,
  distributionCostAdmin: any
) {
  const distributionCost = values.distribution ? distributionCostAdmin : 0;
  const storeCopyConfig = resolveRRDStoreCopyConfig({
    location: values,
    campaign: { storeCopyCpm },
  });

  const isQuantityError = validateStoreCopyQuantity({
    storeCopyQnt: values.store_copy_qnt,
    storeCopy: values.store_copy,
    config: storeCopyConfig,
  });

  const storeCopyCost =
    !isQuantityError && values.store_copy
      ? calculateRRDStoreCopyCost({
          storeCopy: values.store_copy,
          storeCopyQnt: values.store_copy_qnt,
          config: storeCopyConfig,
        })
      : 0;

  const total_cost = (distributionCost || 0) + (storeCopyCost || 0);

  return {
    distribution_cost: distributionCost,
    store_copy_cost: storeCopyCost,
    total_cost: total_cost,
  };
}

/** Bulk Selection */

function isBulkSelectionValid(values: RRDRecord) {
  const notValidMsg = [];

  const hasMailingDate = values.mailing_date;

  const { layoutKey, layoutSpec } = normalizeRRDPrintTypeContext(values);
  const hasDifferentExpiry = supportsRRDDifferentExpiry(layoutKey);
  const hasBrandSetCouponExpiry = isBrandSetCouponExpiration(values);
  const hasCouponExpiry = hasBrandSetCouponExpiry
    ? values.front_coupon_expiry_date
    : values?.different_coupon_expiry && hasDifferentExpiry
      ? values.front_coupon_expiry_date && values.back_coupon_expiry_date
      : values.front_coupon_expiry_date;

  const storeCopyValidationMessage = validateStoreCopyQuantity({
    storeCopyQnt: values.store_copy_qnt,
    storeCopy: values.store_copy,
    storeCopyConfig: values,
  });
  const hasValidStoreCopyQuantity = !storeCopyValidationMessage;

  const hasONDistributionOrStoreCopy = values.distribution || values.store_copy;
  const isStoreCopyWithoutDistribution =
    values.store_copy && !values.distribution;

  const completionAssets = layoutSpec?.completionAssets || {};
  const requiredHeroImages = completionAssets.heroImages || 0;
  const requiredLargeHeroImages = completionAssets.largeHeroImages || 0;
  const requiredMediumHeroImages = completionAssets.mediumHeroImages || 0;
  const requiredCoupons = completionAssets.coupons || 0;

  const hasValidNumberOfHeroImages =
    !requiredHeroImages ||
    getRRDAssetCountFromSelection(values, ASSET_TYPE.HERO) ===
      requiredHeroImages;
  const hasValidNumberOfLargeHeroImages =
    !requiredLargeHeroImages ||
    getRRDAssetCountFromSelection(values, ASSET_TYPE.LARGE_HERO) ===
      requiredLargeHeroImages;
  const hasValidNumberOfMediumHeroImages =
    !requiredMediumHeroImages ||
    getRRDAssetCountFromSelection(values, ASSET_TYPE.MEDIUM_HERO) ===
      requiredMediumHeroImages;
  const hasValidNumberOfCoupons =
    !requiredCoupons ||
    getRRDAssetCountFromSelection(values, ASSET_TYPE.COUPON) ===
      requiredCoupons;

  if (!hasMailingDate) {
    notValidMsg.push("Mailing date should be selected");
  }
  if (!hasCouponExpiry) {
    notValidMsg.push("Coupon expiry should be selected");
  }
  if (!hasONDistributionOrStoreCopy) {
    notValidMsg.push("Distribution or Store Copy should be selected");
  }
  if (isStoreCopyWithoutDistribution) {
    notValidMsg.push("Store Copy requires Distribution to be enabled");
  }
  if (storeCopyValidationMessage) {
    notValidMsg.push(storeCopyValidationMessage);
  }
  if (!hasValidNumberOfCoupons) {
    notValidMsg.push(`Coupons should be ${requiredCoupons}`);
  }
  if (!hasValidNumberOfHeroImages) {
    notValidMsg.push(`Hero images should be ${requiredHeroImages}`);
  }
  if (!hasValidNumberOfLargeHeroImages) {
    notValidMsg.push(`Large hero images should be ${requiredLargeHeroImages}`);
  }
  if (!hasValidNumberOfMediumHeroImages) {
    notValidMsg.push(
      `Medium hero images should be ${requiredMediumHeroImages}`
    );
  }

  const isValid =
    hasValidStoreCopyQuantity &&
    hasONDistributionOrStoreCopy &&
    !isStoreCopyWithoutDistribution &&
    hasMailingDate &&
    hasCouponExpiry &&
    hasValidNumberOfCoupons &&
    hasValidNumberOfHeroImages &&
    hasValidNumberOfLargeHeroImages &&
    hasValidNumberOfMediumHeroImages;

  return {
    isValid,
    notValidMsg,
  };
}

function shouldLocationDisabledToSelect(
  values: RRDRecord,
  location: RRDRecord
) {
  const { print_type, market, mailing_date } = values;

  const {
    print_type: locPrintType,
    market_name: locMarket,
    mailing_weeks: locMailingWeeks,
  } = location;

  if (!print_type) return false;

  if (print_type && !doRRDPrintTypesMatch(print_type, locPrintType))
    return true;

  if (market && market !== locMarket) return true;

  if (
    mailing_date &&
    (!Array.isArray(locMailingWeeks) || !locMailingWeeks.includes(mailing_date))
  )
    return true;

  return false;
}

function getDisableLocationsWhileBulkSelection(
  values: RRDRecord,
  locations: RRDRecord[]
) {
  const disabledLocationsIds = new Set();
  for (const location of locations) {
    if (shouldLocationDisabledToSelect(values, location)) {
      disabledLocationsIds.add(location.id);
    }
  }
  return [...disabledLocationsIds];
}

function getAssetsBelongsToLocation(
  location: RRDRecord,
  selectedAssets: RRDRecord
) {
  const {
    hero_images: selectedHeroImages = [],
    coupons: selectedCoupons = [],
  } = selectedAssets;
  const {
    heroImages: locHeroImages = [],
    largeHeroImages: locLargeHeroImages = [],
    mediumHeroImages: locMediumHeroImages = [],
    coupons: locCoupons = [],
  } = getLocationAssets(location);

  const locHeroIds = new Set(
    [...locHeroImages, ...locLargeHeroImages, ...locMediumHeroImages].map(
      (h) => h.id
    )
  );
  const locCouponIds = new Set(locCoupons.map((c) => c.id));

  const belongingHeroImages = selectedHeroImages.filter((h) =>
    locHeroIds.has(h.id)
  );
  const belongingCoupons = selectedCoupons.filter((c) =>
    locCouponIds.has(c.id)
  );

  const notBelongingHeroDetails = selectedHeroImages
    .filter((h) => !locHeroIds.has(h.id))
    .map((h) => ({
      location_id: location.id,
      location_name: location.location_name,
      asset_name: h.asset_name || h.name,
      asset_id: h.id,
      type: "Hero Image",
    }));

  const notBelongingCouponDetails = selectedCoupons
    .filter((c) => !locCouponIds.has(c.id))
    .map((c) => ({
      location_id: location.id,
      location_name: location.location_name,
      asset_name: c.asset_name || c.name,
      asset_id: c.id,
      type: "Coupon",
    }));

  return {
    belongingHeroImages,
    belongingCoupons,
    notBelongingDetails: [
      ...notBelongingHeroDetails,
      ...notBelongingCouponDetails,
    ],
  };
}

/**
 * Computes weekly availability data from locations
 */
function getWeeklyAvailabilityData(
  locations: RRDLocationCollection = [],
  checkedLocations: any[] = [],
  allMailingWeeks: any[],
  printType?: string | null,
  market?: string | null
) {
  try {
    // Normalize locations
    const locationList = Array.isArray(locations)
      ? locations
      : Object.values(locations);

    if (!locationList.length) return {};

    // Checked locations lookup
    const checkedSet =
      Array.isArray(checkedLocations) && checkedLocations?.length
        ? new Set(checkedLocations)
        : null;

    // Filter locations (single pass)
    const filteredLocations = [];
    for (const loc of locationList) {
      if (printType && !doRRDPrintTypesMatch(printType, loc.print_type))
        continue;
      if (market && market !== loc.market_name) continue;
      if (checkedSet && !checkedSet.has(loc.id)) continue;
      filteredLocations.push(loc);
    }

    if (!filteredLocations.length) return {};

    const totalCount = filteredLocations.length;

    const COLORS = {
      GREEN: "var(--status-success-tint)",
      ORANGE: "var(--status-warning-tint)",
      RED: "var(--status-error-tint)",
    };

    // Map<weekKey, { availableCount, totalCount }>
    const weekAvailabilityMap = new Map();

    // Aggregate availability
    for (const location of filteredLocations) {
      const locMailingWeeks = location.mailing_weeks;
      if (!locMailingWeeks?.length) continue;

      for (const weekDate of locMailingWeeks) {
        const weekMoment = moment(weekDate);
        if (!weekMoment.isValid()) continue;

        const weekKey = weekMoment.startOf("week").format("YYYY-MM-DD");

        let weekData = weekAvailabilityMap.get(weekKey);
        if (!weekData) {
          weekData = {
            availableCount: 0,
            totalCount,
          };
          weekAvailabilityMap.set(weekKey, weekData);
        }
        weekData.availableCount++;
      }
    }

    allMailingWeeks.forEach((weekDate) => {
      const weekMoment = moment(weekDate);
      if (weekMoment.isValid()) {
        const weekStart = weekMoment.clone().startOf("week");
        const weekKey = weekStart.format("YYYY-MM-DD");
        if (!weekAvailabilityMap.has(weekKey)) {
          weekAvailabilityMap.set(weekKey, {
            availableCount: 0,
            totalCount,
          });
        }
      }
    });

    // Convert Map → plain object (for consumers / serialization)
    const result = {};
    for (const [
      weekKey,
      { availableCount, totalCount },
    ] of weekAvailabilityMap) {
      const percentage =
        totalCount > 0 ? (availableCount / totalCount) * 100 : 0;

      result[weekKey] = {
        availableCount,
        totalCount,
        percentage,
        color:
          percentage === 100
            ? COLORS.GREEN
            : percentage >= 75
              ? COLORS.ORANGE
              : COLORS.RED,
        tooltip: `Dates Available for ${availableCount}/${totalCount} Location${
          totalCount !== 1 ? "s" : ""
        }`,
      };
    }

    return result;
  } catch (err) {
    logger.error("Error computing weekly availability data", err);
    return {};
  }
}

function getSingleLocationAvailabilityData(
  mailingWeeks: any[] = [],
  highlightColor = "var(--primary-tint-hover)"
) {
  try {
    const availabilityData = {};

    mailingWeeks.forEach((weekDate) => {
      const weekMoment = moment(weekDate);
      if (weekMoment.isValid()) {
        const weekStart = weekMoment.clone().startOf("week");
        const weekKey = weekStart.format("YYYY-MM-DD");
        availabilityData[weekKey] = {
          color: highlightColor,
          tooltip: "Available Week",
        };
      }
    });

    return availabilityData;
  } catch (err) {
    logger.error("Error creating single location availability data", err);
    return {};
  }
}
